import { Head, router } from '@inertiajs/react'
import React from 'react'
import Layout from './Layout'
import AddOnCard from './Components/AddOnCard'
import DateTimePicker from './Components/DateTimePicker'
import BookingSummary from './Components/BookingSummary'
import { useBooking } from './hooks/useBooking'
import { formatPrice } from './utils/formatters'

export default function Configure({
  account,
  package: pkg,
  add_ons,
  selected_add_on_ids,
  booking_date,
  booking_time,
  party_size,
  venue_id,
  venue_hire_fee_pence = 0,
  blocked_dates = [],
  venue_skipped,
  event_type_id,
  event_type_name,
  event_type_slug,
  meta
}) {
  // Clamp the session prefill into the package's range — a guest arriving with
  // party_size=100 on a 40-max package must see why the number changed, never
  // a silently disabled CTA.
  const minGuests = pkg.min_guests || 1
  const clampedPartySize = Math.min(Math.max(party_size || minGuests, minGuests), pkg.max_guests || Infinity)
  const clampHint = clampedPartySize !== party_size && party_size
    ? (party_size > (pkg.max_guests || Infinity)
      ? `Adjusted to this package's maximum of ${pkg.max_guests} guests`
      : `Adjusted to this package's minimum of ${minGuests} guests`)
    : null

  const {
    selectedAddOnIds,
    toggleAddOn,
    date, setDate,
    time, setTime,
    partySize, setPartySize,
    perPersonTotal,
    flatTotal,
    total
  } = useBooking({
    package: pkg,
    addOns: add_ons,
    initialAddOnIds: selected_add_on_ids,
    initialDate: booking_date,
    initialTime: booking_time,
    initialPartySize: clampedPartySize,
    venueHireFeePence: venue_hire_fee_pence
  })

  const canContinue = date && time && partySize >= minGuests && (!pkg.max_guests || partySize <= pkg.max_guests)
  const disabledHint = !date
    ? 'Choose a date to continue'
    : !time
      ? 'Choose a time to continue'
      : `This package takes ${minGuests}${pkg.max_guests ? `–${pkg.max_guests}` : ' or more'} guests`

  const handleContinue = () => {
    router.post(`/${account.slug}/booking/update`, {
      package_id: pkg.id,
      venue_id: venue_id,
      event_type_id: event_type_id,
      add_on_ids: selectedAddOnIds,
      date,
      time,
      party_size: partySize
    })
  }

  // When the venue step was skipped (single matching package), back should
  // return to the occasion page, not re-trigger the skip. Server-derived —
  // a `from` query param would be lost on validate_booking_session! bounces.
  const backUrl = venue_skipped && event_type_slug
    ? `/${account.slug}/booking/occasions/${event_type_slug}`
    : venue_id
      ? `/${account.slug}/booking/venues/${venue_id}`
      : `/${account.slug}/booking`

  return (
    <>
      <Head title={meta.title} />
      <Layout account={account}>
        {/* Package Hero */}
        <div>
          {pkg.image_url && (
            <div className="h-48 overflow-hidden">
              <img src={pkg.image_url} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className={`px-4 py-6 ${!pkg.image_url ? 'pt-24' : ''}`}>
            <button
              onClick={() => router.visit(backUrl)}
              className="text-white/70 text-sm mb-3 inline-flex items-center hover:text-white transition"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {venue_skipped && event_type_slug ? 'Back to rooms' : venue_id ? 'Back to venue' : 'Back to venues'}
            </button>
            {event_type_name && (
              <p className="text-sky-200 text-sm mb-1">{event_type_name}</p>
            )}
            <h1 className="text-2xl font-bold text-white">{pkg.name}</h1>
            {pkg.description && (
              <p className="text-sky-200 mt-2">{pkg.description}</p>
            )}
            <p className="text-white font-bold mt-2">
              From {formatPrice(pkg.base_price_pence)} per person
            </p>
            {pkg.menu_document_url && (
              <a
                href={pkg.menu_document_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sky-300 text-sm mt-2 hover:text-white transition"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download menu
              </a>
            )}
          </div>
        </div>

        {/* Date, Time & Guests — the commitment question comes first; extras
            are chosen after the date is mentally booked */}
        <div className={`px-4 ${add_ons.length > 0 ? 'pb-6' : 'pb-48'}`}>
          <h2 className="text-lg font-semibold text-white mb-3">When &amp; How Many</h2>
          {clampHint && (
            <p className="text-amber-200 text-sm mb-3" role="status">{clampHint}</p>
          )}
          <DateTimePicker
            date={date}
            onDateChange={setDate}
            time={time}
            onTimeChange={setTime}
            partySize={partySize}
            onPartySizeChange={setPartySize}
            minGuests={minGuests}
            maxGuests={pkg.max_guests}
            blockedDates={blocked_dates}
          />
        </div>

        {/* Add-ons */}
        {add_ons.length > 0 && (
          <div className="px-4 pb-48">
            <h2 className="text-lg font-semibold text-white mb-3">Extras &amp; Add-ons</h2>
            <div className="space-y-3">
              {add_ons.map(addOn => (
                <AddOnCard
                  key={addOn.id}
                  addOn={addOn}
                  selected={selectedAddOnIds.includes(addOn.id)}
                  onToggle={toggleAddOn}
                />
              ))}
            </div>
          </div>
        )}

        <BookingSummary
          total={total}
          perPerson={perPersonTotal}
          partySize={partySize}
          flatTotal={flatTotal}
          venueHireFeePence={venue_hire_fee_pence}
          onContinue={handleContinue}
          disabled={!canContinue}
          disabledHint={disabledHint}
          label="Continue to details"
        />
      </Layout>
    </>
  )
}
